import { booleanAttribute, ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { DsBoxModule, DsMessageModule, DsTagModule } from '@bmw-ds/components';

import { CustomerVehicle } from '../../../customer/model/customer-vehicle.model';
import { useI18n } from '../../../i18n/services/i18n.service';
import { VehicleBoxComponent } from '../vehicle-box/vehicle-box.component';

@Component({
  selector: 'vehicles-overview',
  imports: [DsBoxModule, DsTagModule, DsMessageModule, VehicleBoxComponent],
  template: `
    <div class="grid">
      @for (card of vehicleCards(); track card.vehicle) {
        <cdf-vehicle-box
          class="hide-lt-{{ card.minWidth }}"
          [vehicle]="card.vehicle"
          [activeCountry]="activeCountry()"
          [enableSelection]="enableVehicleSelection()"
          truncateRelations
          smallImage
          (selectVehicle)="selectVehicle.emit(card.vehicle.vin)"
        />
      }
      @for (card of plusXCards(); track card.width) {
        <div
          class="tile-click-target plus-x hide-ne-{{ card.width }}"
          [tabIndex]="enableVehicleSelection() ? 0 : -1"
          (click)="handlePlusXCardClick($event)"
          (keydown.enter)="handlePlusXCardClick($event)"
          (keydown.space)="handlePlusXCardClick($event)"
        >
          <div class="hidden-vehicles">+ {{ card.plusCount }}</div>
        </div>
      }
    </div>
    @if (vehicles() === undefined || vehicles()?.length === 0) {
      <ds-message tone="info" [messageText]="i18n('vehicles.noneFound')" variant="slim" />
    }
  `,
  styleUrl: './vehicles-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VehiclesOverviewComponent {
  protected readonly i18n = useI18n();

  readonly enableVehicleSelection = input(false, { transform: booleanAttribute });
  readonly activeCountry = input<string | undefined>();
  readonly selectVehicle = output<string>();

  // Depending on the width of the component, we can show between 2 and 5 cards.
  // To always show +X cards correctly, we need to know these limits.
  // Keep in sync with SCSS variables.
  readonly minVisibleCards = 2; // css => xs
  readonly maxVisibleCards = 5; // css => l

  readonly plusXCardClick = output();

  readonly vehicles = input<undefined | CustomerVehicle[]>();

  readonly vehicleCards = computed(() => {
    const vehicles = this.vehicles() ?? [];
    const count = vehicles.length === this.maxVisibleCards ? this.maxVisibleCards : this.maxVisibleCards - 1;
    return (this.vehicles() ?? []).slice(0, count).map((vehicle, index) => ({
      vehicle,
      minWidth: Math.min(index + 2, vehicles.length),
    }));
  });

  /**
   * Calculates how many +X cards need to be shown based on the number of vehicles and the visible card limits.
   * There are at most "maxVisibleCards - minVisibleCards + 1" plusX cards that we needed to show.
   * These cards with the different numbers will be shown/hidden based on the width of the component using CSS.
   */
  readonly plusXCards = computed(() => {
    const vehicleCount = this.vehicles()?.length ?? 0;
    const maxWidthWithPlus = Math.min(vehicleCount - 1, this.maxVisibleCards);

    return Array.from({ length: maxWidthWithPlus - this.minVisibleCards + 1 }, (_v, i) => {
      const width = this.minVisibleCards + i;
      return { width: width, plusCount: vehicleCount - width + 1 };
    });
  });

  protected handlePlusXCardClick(event: Event) {
    if (this.enableVehicleSelection()) {
      event.stopPropagation();
      this.plusXCardClick.emit();
    }
  }
}
